/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';

import { CommunicationAttachmentDto } from '../../communications';
import { ContactDeliveryMethod } from '../../contacts/model/contact-details-shared.dto';
import { JobVariationStatus } from '../job-change-orders/job-change-order.dto';
import { JobFinanceType } from '../job-general-info/job-general-info.dto';
import { InvoiceStatus } from '../job-financials';

export const InvoiceType = createEnum('INVOICE_TYPE', {
  Draw: 1,
  ChangeOrder: 2,
  CreditNote: 3,
});
export type InvoiceType = EnumSet<typeof InvoiceType>;

export const DrawInvoiceStatus = createEnum('INVOICE_STATUS', {
  Draft: 1,
  Submitted: 2,
  Authorized: 3,
  Deleted: 4,
  Voided: 5,
  Paid: 6,
});
export type DrawInvoiceStatus = EnumSet<typeof DrawInvoiceStatus>;

export const InvoicePhysicalType = createEnum('INVOICE_PHYSICAL_TYPE', {
  Accrec: 1,
  Accreccreadit: 2,
  Overpayment: 3,
});

export type InvoicePhysicalType = EnumSet<typeof InvoicePhysicalType>;

export type JobDrawsPaymentDto = {
  invoiceId: ID;
  paymentId: ID;
  paidDate: Date;
  amount: number;
  reference: string;
};

export type JobDrawsInvoiceDto = {
  invoiceId: ID;
  invoiceNumber: string;
  accountingDate: Date;
  type: InvoiceType;
  invoiceType: InvoicePhysicalType;
  status: DrawInvoiceStatus;
  statusDescription: string;
  description: string;
  amountWithGst: number;
  balance: number;
  externalId: ID;
  payments: JobDrawsPaymentDto[];
  xeroUrl: string;
  isNotOpening: boolean;
  variationId?: ID;
  drawRuleId: ID;
  drawRuleName: string;
  drawRuleStage?: number;
  isCreatedFromChangeOrder: boolean;
};

export type JobDrawsLendersScheduleItemDto = {
  code: number;
  name: string;
  orderNumber: number;
  orderCost: number;
  isExcluded: boolean;
  amountDrawn: number;
  balance: number;
};

export type JobDrawsPaymentScheduleItemDto = {
  name: string;
  pctDue: number;
  amountDue: number;
  isDeposit: boolean;
  isDeductDeposit: boolean;
  amountDueWithGst: number;
};

export type JobDrawsRuleDto = WithVersion<{
  id: ID;
  code: string;
  name: string;
}>;

export type JobDrawsDto = WithVersion<{
  drawRuleId: ID;
  drawRules: JobDrawsRuleDto[];
  invoices: JobDrawsInvoiceDto[];
  lendersSchedule: JobDrawsLendersScheduleItemDto[];
  paymentSchedule: JobDrawsPaymentScheduleItemDto[];
  totalToBe: number;
  totalAmount: number;
  contractToBe: number;
  contractAmount: number;
  contractAmountLenders: number;
  variationsToBe: number;
  totalDrawnAmount: number;
  variationsAmount: number;
  totalAmountWithGst: number;
  contractDrawnAmount: number;
  contractAmountWithGst: number;
  variationsDrawnAmount: number;
  variationsAmountWithGst: number;
  isNotProceeding: boolean;
  isFinalized: boolean;
  isLenderScheduleAllowed: boolean;
}>;

export type JobDrawsRecalculateRequestDto = WithVersion<{
  drawRuleId?: ID;
  contractAmount: number;
  contractAmountWithGst: number;
  isContractAmountManual: boolean;
  isContractAmountWithGstManual: boolean;
  isContractPriceUpdate: boolean;
  isSave: boolean;
}>;

export type JobDrawDetailsPaymentDto = {
  invoiceId: ID;
  paymentId: ID;
  paidDate: Date;
  amount: number;
  reference: string;
};

export type JobDrawDetailsCurrentInvoiceDto = {
  variationId: ID;
  drawRuleId: ID;
  drawRuleName: string;
  drawRuleStage: number;
  drawRuleStageName: string;
  seqNumber?: number;
  invoiceNumber: number;
  amount: number;
  amountWithGst: number;
  accountingDate: Date;
  correction: number;
  taxType?: ID;
  taxName?: string;
  taxRate?: number;
  memo: string;
  lendersScheduleCost: number;
  lendersScheduleCostIncluded: number;
  contractAmount: number;
  contractAmountWithGst: number;
  contractAmountLenders: number;
  historyProgressClaims: number;
  historyVarsClaims: number;
  historyPaid: number;
  historyCreditNotes: number;
  historyTotalDue: number;
  historyInvoiced: number;
  historyRecAmount: number;
  historyRecGst: number;
  historyAreaMemo: string;
};

export type JobDrawDetailsInvoiceDto = {
  invoiceId: ID;
  variationId: ID;
  drawRuleId: ID;
  drawRuleName: string;
  drawRuleStage: number;
  seqNumber?: number;
  invoiceNumber: number;
  amount: number;
  amountWithGst: number;
  accountingDate: Date;
  type: InvoiceType;
  invoiceType: InvoicePhysicalType;
  status: InvoiceStatus;
  description: string;
  balance: number;
  externalId: ID;
  xeroUrl: string;
  payments: JobDrawDetailsPaymentDto[];
};

export type JobDrawDetailsChangeOrderDto = WithVersion<{
  variationId: ID;
  variationNumber: number;
  drawRuleId: ID;
  drawRuleName: string;
  drawRuleStage: number;
  description: string;
  authorizedDate: Date;
  amount: number;
  amountWithGst: number;
  gstAmount: number;
  status: JobVariationStatus;
}>;

export type JobDrawDetailsPaymentScheduleItemDto = {
  name: string;
  pctDue: number;
  amountDue: number;
  amountDueWithGst: number;
  stageNumber: number;
  isDeposit: boolean;
  isDeductDeposit: boolean;
  thisDraw: number;
};

export type JobDrawDetailsLendersScheduleItemDto = {
  code: number;
  name: string;
  balance: number;
  orderCost: number;
  amountDrawn: number;
  orderNumber: number;
  orderCostPct: number;
  contractCostPct: number;
  thisDraw?: number;
  isExcluded: boolean;
};

export type JobDrawDetailsStageDto = {
  id: ID;
  name: string;
  stageNumber: number;
};

export type JobDrawDetailsDto = WithVersion<{
  jobId: ID;
  drawRuleId: ID;
  jobNumber: string;
  siteAddress: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerMethod: ContactDeliveryMethod;
  totalPaid: number;
  totalCreditNotes: number;
  totalBalance: number;
  totalToBe: number;
  totalAmount: number;
  contractToBe: number;
  contractAmount: number;
  variationsToBe: number;
  totalDrawnAmount: number;
  variationsAmount: number;
  totalAmountWithGst: number;
  contractDrawnAmount: number;
  contractAmountWithGst: number;
  variationsDrawnAmount: number;
  variationsAmountWithGst: number;
  costLendIncluded: number;
  costLend: number;
  invoice?: JobDrawDetailsCurrentInvoiceDto;
  drawRuleCode: string;
  drawRuleName: string;
  invoices: JobDrawDetailsInvoiceDto[];
  variations: JobDrawDetailsChangeOrderDto[];
  paymentSchedule: JobDrawDetailsPaymentScheduleItemDto[];
  lendersSchedule: JobDrawDetailsLendersScheduleItemDto[];
  drawRuleStages: JobDrawDetailsStageDto[];
  financeCompanyAddress: string;
  financeCompanyName: string;
  financeType: JobFinanceType;
  isQuoteVariationExist: boolean;
  taxType: ID;
  taxName: string;
  taxRate: number;
  gstApplies: boolean;
}>;

export type JobDrawCreatePaymentScheduleRequestDto = WithPrivilegedToken<WithOptimisticLock<{
  jobId: ID;
  contractDrawnAmount: number;
  variationsDrawnAmount: number;
  totalPaid: number;
  totalCreditNotes: number;
  historyAreaMemo: string;
  totalDue: number;
  invoicedToDate: number;
  recAmount: number;
  recGst: number;
  contractAmountWithGst: number;
  contractAmount: number;
  accountingDate: Date;
  drawRuleId: ID;
  correction: number;
  drawStageNumber: number;
  drawAmount: number;
  drawAmountWithGst: number;
  drawRecAmountWithGst: number;
  taxType?: ID;
  taxName?: string;
  taxRate?: number;
  memo: string;
  variations: JobDrawDetailsChangeOrderDto[];
  isPreview: boolean;
}>>;

export type JobDrawCreateLendersScheduleRequestDto = {
  contractDrawnAmount: number;
  variationsDrawnAmount: number;
  totalPaid: number;
  totalCreditNotes: number;
  historyAreaMemo: string;
  accountingDate: Date;
  contractAmount: number;
  contractAmountLenders: number;
  costLend: number;
  costLendIncluded: number;
  totalThisDraw: number;
  lendersSchedule: JobDrawsLendersScheduleItemDto[];
  isPreview: boolean;
};

export type JobDrawCreateRequestProgressDto = Record<string, never>;

export type JobDrawCalculateRequestDto = {
  amount: number;
  gstPct: number;
  correction: number;
};

export type JobDrawCalculateResponseDto = {
  amountWithGst: number;
};

export type JobDrawPrintRequestDto = {
  printerId: ID;
  authCode: string;
  generatedDocs: CommunicationAttachmentDto[];
};

export type JobDrawGeneratedDocsResponseDto = {
  docs: CommunicationAttachmentDto[];
  preview: boolean;
};
